import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, fmt, timeAgo } from "@/lib/api";

const notify = (a) => {
  if (typeof Notification !== "undefined" && Notification.permission === "granted")
    new Notification(`Fx Cotrader · ${a.direction} ${a.symbol}`, { body: `${a.quality} quality · ${a.confidence}% confidence · R:R 1:${a.risk_reward}` });
};

export default function AlertBell() {
  const [data, setData] = useState(null);
  const latest = useRef(null);
  const nav = useNavigate();
  useEffect(() => {
    const load = async () => {
      try {
        const { data: d } = await api.get("/alerts");
        setData(d);
        const newest = d.alerts[0]?.created_at;
        if (latest.current && d.enabled) {
          d.alerts.filter((a) => a.created_at > latest.current).forEach((a) => {
            toast(`New ${a.quality} signal: ${a.direction} ${a.symbol}`, {
              description: `${a.confidence}% confidence · R:R 1:${a.risk_reward}`,
              action: { label: "View", onClick: () => nav(`/signal/${a.symbol.replace("/", "")}`) },
            });
            if (d.browser_notifications) notify(a);
          });
        }
        latest.current = newest || latest.current || "0";
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [nav]);
  const markSeen = async (open) => {
    if (!open || !data?.unread) return;
    await api.post("/alerts/seen");
    setData({ ...data, unread: 0 });
  };
  const dg = (s) => (s.endsWith("JPY") ? 3 : 5);
  return (
    <Popover onOpenChange={markSeen}>
      <PopoverTrigger asChild>
        <button className="relative text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="alert-bell-btn" title="Signal alerts">
          <Bell size={16} />
          {data?.unread > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#DC2626] text-white text-[9px] font-bold grid place-items-center" data-testid="alert-unread-count">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-sm" data-testid="alert-panel">
        <div className="px-3 py-2 border-b border-zinc-200 flex justify-between items-center">
          <span className="label-xs">High-confidence alerts</span>
          {!data?.enabled && <span className="text-[10px] text-[#D97706]">Disabled in Settings</span>}
        </div>
        <div className="max-h-80 overflow-y-auto scrollbar-thin">
          {(data?.alerts || []).map((a, i) => (
            <button key={i} onClick={() => nav(`/signal/${a.symbol.replace("/", "")}`)} className="w-full text-left px-3 py-2.5 border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`alert-item-${i}`}>
              <div className="flex justify-between items-center">
                <span className="num text-sm font-bold">{a.symbol} <span className={a.direction === "BUY" ? "text-[#059669]" : "text-[#DC2626]"}>{a.direction}</span></span>
                <span className="num text-xs text-[#7C3AED]">{a.confidence}%</span>
              </div>
              <div className="flex justify-between text-[11px] text-zinc-500 mt-0.5">
                <span>{a.quality} · entry {fmt(a.entry, dg(a.symbol))} · 1:{a.risk_reward}</span><span>{timeAgo(a.created_at)}</span>
              </div>
            </button>
          ))}
          {!data?.alerts?.length && <p className="p-4 text-xs text-zinc-500">No High or Very High signals yet. You'll be alerted here instantly.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
